var CartDrawer = customElements.get('cart-drawer') || class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items preserves the official manual cart:view trigger when
    // Standard Events is available; analytics never blocks the required drawer.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    try {
      this.replaceCartSections(parsedState.sections);
    } catch (error) {
      // A successful add may still return null sections. Retry only the read,
      // never the add, retaining the old DOM until every section is valid.
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', this.getSectionsToRender().map((section) => section.id).join(','));
        const response = await fetch(url.pathname + url.search, { cache: 'no-store' });
        if (!response.ok) throw new Error('Cart section recovery failed');
        this.replaceCartSections(await response.json());
      } catch (recoveryError) {
        console.error(recoveryError);
        const errors = this.querySelector('#CartDrawer-CartErrors');
        if (errors) {
          const link = document.createElement('a');
          link.href = routes.cart_url;
          link.textContent = window.cartStrings.error;
          errors.replaceChildren(link);
        }
      }
    }
    this.productId = parsedState.id;
    this.open();
  }

  replaceCartSections(sections) {
    const replacements = this.getSectionsToRender().map((section) => {
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const html = this.getSectionInnerHTML(sections?.[section.id], section.selector);
      if (!target) throw new Error('Missing cart mount');
      return { target, html };
    });
    replacements.forEach(({ target, html }) => { target.innerHTML = html; });
    this.classList.toggle('is-empty', !!this.querySelector('.drawer__inner-empty'));
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string' || !html.trim()) throw new Error('Missing cart section');
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!source) throw new Error('Invalid cart section');
    return source.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
};

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

function defineCartDrawerItems() {
  if (customElements.get('cart-drawer-items')) return;

  const CartItemsBase = customElements.get('cart-items');
  if (!CartItemsBase) return;

  class CartDrawerItems extends CartItemsBase {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}

if (customElements.get('cart-items')) {
  defineCartDrawerItems();
} else {
  customElements.whenDefined('cart-items').then(defineCartDrawerItems);
}
